import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { LocalizedLink } from "@/components/LocalizedLink";
import { navigateToLocale } from "@/components/LocaleUrlSync";
import { useLocale } from "@/locale/LocaleProvider";
import { pickOfferSite } from "@/locale/content/offerSite";
import { LANG_SWITCHER } from "@/locale/appLang";
import { SITE_TELEGRAM_URL } from "@/lib/siteContact";
import { trackContactClick } from "@/lib/analytics";

const LANGS = LANG_SWITCHER;

export default function SiteNavbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, t } = useLocale();
  const copy = pickOfferSite(lang);

  const items = [
    { label: copy.nav.services, href: "/#products" },
    { label: copy.nav.cases, href: "/cases" },
    { label: copy.nav.about, href: "/about" },
    { label: copy.nav.contacts, href: "/contacts" },
  ];

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <nav
      className="fixed inset-x-0 top-0 z-50 border-b border-[var(--a-border)]/80 bg-[var(--a-bg)]/85 backdrop-blur-xl"
      aria-label={t("nav.ariaMain")}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <LocalizedLink
          to="/"
          className="font-display text-lg font-semibold tracking-tight text-[var(--a-fg)]"
          onClick={() => setOpen(false)}
          aria-label={t("nav.logoAria")}
        >
          IT<span className="text-[var(--a-primary)]">-24</span>
        </LocalizedLink>

        <ul className="hidden items-center gap-1 md:flex">
          {items.map((item) => (
            <li key={item.href}>
              <LocalizedLink
                to={item.href}
                className="rounded-lg px-3 py-2 text-sm font-medium text-[var(--a-muted)] transition duration-200 hover:bg-white/[0.04] hover:text-[var(--a-fg)]"
              >
                {item.label}
              </LocalizedLink>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-3 md:flex">
          <div className="flex gap-0.5 rounded-lg border border-[var(--a-border)] p-0.5" role="group" aria-label={t("nav.langSwitcherAria")}>
            {LANGS.map(({ code, label }) => (
              <button
                key={code}
                type="button"
                aria-pressed={lang === code}
                onClick={() => navigateToLocale(navigate, location.pathname, code)}
                className={`min-h-8 min-w-9 rounded-md px-2 text-xs font-semibold transition duration-200 ${
                  lang === code
                    ? "bg-[var(--a-primary)] text-[var(--a-on-primary)]"
                    : "text-[var(--a-muted)] hover:text-[var(--a-fg)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <a
            href={SITE_TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackContactClick("telegram")}
            className="inline-flex min-h-10 items-center rounded-xl bg-[var(--a-primary)] px-3.5 text-sm font-semibold text-[var(--a-on-primary)] transition duration-200 hover:bg-[var(--a-primary-hover)]"
          >
            {copy.nav.telegramCta}
          </a>
        </div>

        <button
          type="button"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-[var(--a-fg)] md:hidden"
          aria-label={open ? t("nav.menuClose") : t("nav.menuOpen")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open ? (
        <div className="border-t border-[var(--a-border)] bg-[var(--a-bg)] px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            {items.map((item) => (
              <LocalizedLink
                key={item.href}
                to={item.href}
                className="min-h-11 rounded-lg px-3 py-2.5 text-[var(--a-fg)] hover:bg-white/[0.04]"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </LocalizedLink>
            ))}
            <div className="mt-3 flex gap-2">
              {LANGS.map(({ code, label }) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    navigateToLocale(navigate, location.pathname, code);
                    setOpen(false);
                  }}
                  className={`min-h-11 flex-1 rounded-xl text-sm font-semibold ${
                    lang === code
                      ? "bg-[var(--a-primary)] text-[var(--a-on-primary)]"
                      : "border border-[var(--a-border)] text-[var(--a-fg)]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <a
              href={SITE_TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackContactClick("telegram");
                setOpen(false);
              }}
              className="mt-3 inline-flex min-h-11 items-center justify-center rounded-xl bg-[var(--a-primary)] text-sm font-semibold text-[var(--a-on-primary)]"
            >
              {copy.nav.telegramCta}
            </a>
          </div>
        </div>
      ) : null}
    </nav>
  );
}
